import { useState, useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Circle, Marker, Polyline, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet-polylinedecorator';
import { renderToStaticMarkup } from 'react-dom/server';
import { Home, Radar, Truck } from 'lucide-react';
import api from '../../services/api';
import { API } from '../../App';
import { RUN_COLORS } from './runColors';
import BranchMarkersLayer from './BranchMarkersLayer';
import { isEntryComplete, isEntryFinished } from './entryStatus';
import { orderRunEntries } from './stopNumbers';
import 'leaflet/dist/leaflet.css';

const ORANGE = '#ee7203';
const PURPLE = '#a855f7';
const LIVE_BLUE = '#2563eb';
// Green, used ONLY for the "driving back to base" leg (between runs, and after the last drop of the day) — deliberately
// not tied to any run's own colour, so it can never be mistaken for that run continuing.
const RETURN_TO_BASE_COLOR = '#22c55e';
// A completed stop's dot turns this green.
const STOP_GREEN = '#22c55e';

// A map tile that fails to load (a brief network or tile-server hiccup, common when zooming in fast) is asked for again, up to
// twice a moment apart, instead of being left as a hole in the map.
const retryFailedTile = (e) => {
  const tile = e && e.tile;
  if (!tile || !tile.src) return;
  const attempts = Number(tile.dataset.c30Retries || 0);
  if (attempts >= 2) return;
  tile.dataset.c30Retries = String(attempts + 1);
  const source = tile.src;
  setTimeout(() => { tile.src = ''; tile.src = source; }, 700 * (attempts + 1));
};

// The beacon: when a postcode is clicked on a Transport entry, that stop's dot gets a pulsing shadow behind it for a few seconds.
const BEACON_CSS = `
@keyframes c30-stop-beacon { 0% { transform: scale(1); opacity: 0.85; } 100% { transform: scale(3.4); opacity: 0; } }
.c30-stop-beacon { animation: c30-stop-beacon 1.3s ease-out infinite; }
.c30-stop-beacon-2 { animation: c30-stop-beacon 1.3s ease-out 0.65s infinite; }
`;

const stopIconCache = new Map();
/** One stop's dot: its run's colour (green once the stop is complete) with the stop's number in it — the same number the
 *  entry card shows in its bottom-right corner — and, while `beacon` is on, a pulsing shadow behind it. Icons are cached so
 *  the map does not rebuild every dot each time the board refreshes. */
const getStopIcon = (color, number, complete, beacon) => {
  const key = `${color}|${number ?? ''}|${complete ? 1 : 0}|${beacon ? 1 : 0}`;
  if (stopIconCache.has(key)) return stopIconCache.get(key);
  const fill = complete ? STOP_GREEN : color;
  const icon = L.divIcon({
    html: renderToStaticMarkup(
      <div style={{ position: 'relative', width: 26, height: 26, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {beacon && (
          <>
            <div className="c30-stop-beacon" style={{ position: 'absolute', width: 26, height: 26, borderRadius: '50%', background: fill, boxShadow: `0 0 14px 4px ${fill}` }} />
            <div className="c30-stop-beacon c30-stop-beacon-2" style={{ position: 'absolute', width: 26, height: 26, borderRadius: '50%', background: fill }} />
          </>
        )}
        <div style={{ position: 'relative', width: 22, height: 22, borderRadius: '50%', background: fill, border: '2px solid white', boxShadow: '0 1px 4px rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: number != null && number > 9 ? 10 : 11, fontWeight: 800, lineHeight: 1 }}>
          {number != null ? number : ''}
        </div>
      </div>
    ),
    className: '',
    iconSize: [26, 26],
    iconAnchor: [13, 13],
    popupAnchor: [0, -13],
  });
  stopIconCache.set(key, icon);
  return icon;
};

// Simple in-memory cache so we don't re-hit postcodes.io for the same postcode twice per session
const geocodeCache = new Map();
const fetchLocation = async (postcode) => {
  if (!postcode) return null;
  const clean = postcode.trim().toUpperCase();
  if (!clean) return null;
  if (geocodeCache.has(clean)) return geocodeCache.get(clean);
  try {
    const res = await api.get(`${API}/postcode/${encodeURIComponent(clean.replace(/\s+/g, ''))}`);
    geocodeCache.set(clean, res.data);
    return res.data;
  } catch {
    geocodeCache.set(clean, null);
    return null;
  }
};

const homeIcon = L.divIcon({
  html: renderToStaticMarkup(
    <div style={{ background: ORANGE, width: 30, height: 30, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px solid white', boxShadow: '0 2px 6px rgba(0,0,0,0.4)' }}>
      <Home size={16} color="white" strokeWidth={2.5} />
    </div>
  ),
  className: '',
  iconSize: [30, 30],
  iconAnchor: [15, 15],
});

/** Live truck marker — always blue (rotates to heading), pulsing ring + a
 *  badge showing the last 3 characters of the vehicle's registration (so
 *  which specific truck this is reads at a glance when several are on the
 *  map at once) — reads unmistakably as a real-time position, never
 *  confused with a static planned-route pin.
 *  `haloColor` — set when the vehicle has genuinely arrived+stopped at a specific
 *  drop — is that STOP'S OWN run colour (not a generic amber), so the coloured
 *  stop pin effectively "moves into" the truck (its separate static pin is hidden
 *  while this is showing — see the caller).
 *  `isHome` — set when the vehicle is stopped back at base — swaps the truck
 *  glyph for a house glyph (still blue, unlike the static orange home
 *  pin) so a stationary-at-base vehicle reads at a glance, no rotation applied
 *  since a house has no facing direction. */
const buildLiveTruckIcon = (heading, haloColor, isHome, regSuffix) => L.divIcon({
  html: renderToStaticMarkup(
    <div style={{ position: 'relative', width: 56, height: 56, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      {haloColor && (
        <div style={{ position: 'absolute', width: 52, height: 52, borderRadius: '50%', background: haloColor, opacity: 0.32, border: `2px solid ${haloColor}` }} />
      )}
      <div className="samsara-live-pulse-ring" style={{ position: 'absolute', width: 40, height: 40, borderRadius: '50%', background: LIVE_BLUE, opacity: 0.35 }} />
      <div style={{ position: 'relative', width: 28, height: 28, borderRadius: '50%', background: haloColor || LIVE_BLUE, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px solid white', boxShadow: '0 2px 8px rgba(0,0,0,0.5)', transform: isHome ? 'none' : `rotate(${heading || 0}deg)` }}>
        {isHome ? (
          <Home size={15} color="white" strokeWidth={2.5} />
        ) : (
          <Truck size={15} color="white" strokeWidth={2.5} style={{ transform: `rotate(${-(heading || 0)}deg)` }} />
        )}
      </div>
      {regSuffix && (
        <div style={{ position: 'absolute', top: 2, left: '50%', transform: 'translateX(-50%)', background: LIVE_BLUE, color: 'white', fontSize: 8, fontWeight: 800, letterSpacing: '0.5px', padding: '1px 4px', borderRadius: 4, boxShadow: '0 1px 3px rgba(0,0,0,0.4)' }}>
          {regSuffix}
        </div>
      )}
    </div>
  ),
  className: '',
  iconSize: [56, 56],
  iconAnchor: [28, 28],
});

const MILES_TO_METRES = 1609.34;
const AT_BASE_RADIUS_MILES = 0.3;
// Mirrors TransportNewUI's own TERMINAL_ENTRY_STATUSES / the backend's _TERMINAL_STATUSES —
// kept in sync by hand since this component doesn't import from either.
// Which stops count as finished lives in ./entryStatus.js (a stock transfer is finished once it has a completed_at).

const haversineMiles = (lat1, lng1, lat2, lng2) => {
  const R = 3958.8;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
};

/** Recenters/fits the map when the base location or route first loads for a given
 *  vehicle/day (`fitKey`) — but NEVER again after that, once the user has manually
 *  panned or zoomed, even as the live vehicle keeps moving or the board silently
 *  refreshes every few seconds (both of which create new `base`/`points` object
 *  references without the user actually switching vehicle/day). Only a genuine
 *  `fitKey` change (a different vehicle selected, or the date/mode switched) resets
 *  that "leave it alone" flag and re-fits for the new context. Manual vs. programmatic
 *  moves are told apart via an `isProgrammaticRef` guard, since Leaflet's own
 *  `movestart`/`zoomstart` events fire for both. */
const MapAutoFit = ({ base, points, fitKey, focusNonce = 0 }) => {
  // Set when a postcode is clicked on a Transport entry: the map is then centred on that stop (see MapFocus) and must not be
  // re-fitted away from it by the board's refreshes. It lapses as soon as a different vehicle / day is shown.
  const focusLockRef = useRef({ nonce: 0, fitKey: null });
  const map = useMap();
  const userInteractedRef = useRef(false);
  const isProgrammaticRef = useRef(false);
  const lastFitKeyRef = useRef(null);

  useEffect(() => {
    const onUserMove = () => {
      if (!isProgrammaticRef.current) userInteractedRef.current = true;
    };
    map.on('movestart', onUserMove);
    map.on('zoomstart', onUserMove);
    return () => {
      map.off('movestart', onUserMove);
      map.off('zoomstart', onUserMove);
    };
  }, [map]);

  useEffect(() => {
    if (!base) return;
    if (focusNonce && focusNonce !== focusLockRef.current.nonce) focusLockRef.current = { nonce: focusNonce, fitKey };
    if (lastFitKeyRef.current !== fitKey) {
      lastFitKeyRef.current = fitKey;
      userInteractedRef.current = false; // switched vehicle/day — allowed to auto-fit again
      if (focusLockRef.current.fitKey !== fitKey) focusLockRef.current = { ...focusLockRef.current, fitKey: null };
    }
    if (focusLockRef.current.fitKey === fitKey) return;
    if (userInteractedRef.current) return; // user has manually panned/zoomed — leave their view alone
    const latlngs = [[base.lat, base.lng], ...points.map((p) => [p.lat, p.lng])];
    isProgrammaticRef.current = true;
    if (latlngs.length === 1) {
      map.setView(latlngs[0], 9);
    } else {
      map.fitBounds(latlngs, { padding: [40, 40], maxZoom: 11 });
    }
    map.once('moveend', () => { isProgrammaticRef.current = false; });
  }, [base, points, map, fitKey, focusNonce]);
  return null;
};

/** Builds a gently-curved path through a sequence of [lat,lng] points using
 *  quadratic-bezier interpolation per segment — keeps travel order intact,
 *  just bows each leg slightly to one side instead of a dead-straight line. */
const buildCurvedPath = (points, bend = 0.12) => {
  if (points.length < 2) return points;
  const curved = [points[0]];
  for (let i = 0; i < points.length - 1; i++) {
    const [lat0, lng0] = points[i];
    const [lat2, lng2] = points[i + 1];
    const midLat = (lat0 + lat2) / 2;
    const midLng = (lng0 + lng2) / 2;
    const dLat = lat2 - lat0;
    const dLng = lng2 - lng0;
    const dist = Math.sqrt(dLat * dLat + dLng * dLng) || 0.0001;
    // perpendicular unit vector, offset the midpoint to create the bow
    const nLat = -dLng / dist;
    const nLng = dLat / dist;
    const ctrlLat = midLat + nLat * dist * bend;
    const ctrlLng = midLng + nLng * dist * bend;
    const segments = 14;
    for (let s = 1; s <= segments; s++) {
      const t = s / segments;
      const lat = (1 - t) * (1 - t) * lat0 + 2 * (1 - t) * t * ctrlLat + t * t * lat2;
      const lng = (1 - t) * (1 - t) * lng0 + 2 * (1 - t) * t * ctrlLng + t * t * lng2;
      curved.push([lat, lng]);
    }
  }
  return curved;
};

/** A postcode was clicked on a Transport entry: glide the map so that stop sits in the middle of the screen. The zoom is kept,
 *  but never further out than city level, so the stop is always clearly visible. */
const MapFocus = ({ target }) => {
  const map = useMap();
  useEffect(() => {
    if (!target) return;
    map.flyTo([target.lat, target.lng], Math.max(map.getZoom(), 10), { duration: 0.9 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target?.key]);
  return null;
};

/** Keeps Leaflet's idea of the map's size in step with the real one. Leaflet only re-measures the map when the browser window is
 *  resized, but the page is also resized WITHOUT that happening (the laptop page zoom switching on, a scrollbar appearing, the
 *  board layout changing). A map that thinks it is smaller than it really is only loads map tiles for that smaller area, so the
 *  rest of the screen keeps showing the coarser tiles of the previous zoom — the patchwork. Re-measuring when the map's box
 *  changes size, and a few times just after it first appears, stops that. It does nothing when the size has not changed. */
const MapSizeWatcher = () => {
  const map = useMap();
  useEffect(() => {
    const container = map.getContainer();
    let timer = null;
    const remeasure = () => {
      clearTimeout(timer);
      timer = setTimeout(() => map.invalidateSize({ pan: false }), 120);
    };
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(remeasure) : null;
    if (observer) observer.observe(container);
    window.addEventListener('resize', remeasure);
    const settle = [200, 800, 2000].map((ms) => setTimeout(() => map.invalidateSize({ pan: false }), ms));
    return () => {
      clearTimeout(timer);
      settle.forEach(clearTimeout);
      window.removeEventListener('resize', remeasure);
      if (observer) observer.disconnect();
    };
  }, [map]);
  return null;
};

/** Renders one route: a soft drop-shadow line underneath a coloured curved
 *  line, plus direction-of-travel arrowheads via leaflet-polylinedecorator
 *  (imperative Leaflet layer — no React wrapper exists for it). */
const RouteLine = ({ path, color, dashed = false }) => {
  const map = useMap();
  useEffect(() => {
    if (!map || path.length < 2) return;
    const line = L.polyline(path, { color, weight: 0, opacity: 0 }).addTo(map);
    const decorator = L.polylineDecorator(line, {
      patterns: [
        {
          offset: '8%',
          repeat: '22%',
          symbol: L.Symbol.arrowHead({
            pixelSize: 9,
            polygon: true,
            pathOptions: { color: '#ffffff', fillColor: '#ffffff', fillOpacity: 1, weight: 1, opacity: 1 },
          }),
        },
      ],
    }).addTo(map);
    return () => {
      map.removeLayer(decorator);
      map.removeLayer(line);
    };
  }, [map, path, color]);

  return (
    <>
      {/* Drop-shadow pass — thicker, dark, blurred underneath the main line */}
      <Polyline positions={path} pathOptions={{ color: '#000000', weight: 6, opacity: 0.25, className: 'route-line-shadow' }} />
      {/* Main coloured route line, slightly curved — "returning to base" legs use a
          neutral grey/white dashed style instead of a run colour, so a driver heading
          back to the depot never looks like he's just carrying straight on into the
          next run. */}
      <Polyline
        positions={path}
        pathOptions={{
          color, weight: 3.5, opacity: 0.95, lineCap: 'round', lineJoin: 'round',
          ...(dashed ? { dashArray: '10 8' } : {}),
        }}
      />
    </>
  );
};

/** Splits a day's full breadcrumb history into coloured, connected segments:
 *
 *  - COLOUR bands by physical out-and-back cycle from base — Run 1's colour from
 *    the moment the vehicle first leaves base, switching to Run 2's the moment it
 *    next returns-to-base-then-departs-again, and so on (mirrors the Run
 *    1=Orange/2=Purple/3=Pink/4=Blue scheme used everywhere else in the app).
 *  - STYLE: every segment before the day's last genuinely-reached stop (from
 *    `completedStops`, which — deliberately — only includes GPS-confirmed
 *    geofence arrivals, not manual "mark complete" clicks with no real position/time
 *    to place on the trail) renders dashed; only the trailing segment, from that
 *    last stop up to the live position, renders solid — `markLastSegmentSolid`
 *    (only true for 'today' with a live signal) gates this: a finished/past day has
 *    no "current position" concept, so the whole thing renders dashed instead.
 */
/** Physical out-and-back cycle index (0-based, Run-1=0/Run-2=1/etc) for each
 *  breadcrumb point — purely from where the vehicle has REALLY physically been today
 *  (GPS), independent of the scheduled run order. Shared by `buildBreadcrumbSegments`
 *  (colouring the trail) and the "ahead of vehicle" reveal logic below (deciding
 *  whether a driver who has ALREADY physically returned-to-base-and-left-again still
 *  needs a "heading back to base" leg drawn, or whether that already happened and the
 *  direct leg onward to the next stop is the correct thing to show instead).
 *  `completedStopTimes` — GPS-confirmed drop-arrival timestamps for the day (see
 *  services/samsara_geofence.py) — gate whether a return-to-base is treated as a
 *  genuinely FINISHED run at all: a there-and-back trip that never actually reached a
 *  single stop (forgotten paperwork, fuel, an aborted run) isn't a completed run, so it
 *  must NOT bump the colour onward to the next one either — the vehicle just carries on
 *  as the SAME run once it heads back out. */
const MIN_STATE_DWELL_MINUTES = 3;

const computeRunCycleByPoint = (points, baseLocation, completedStopTimes = []) => {
  if (!points || points.length === 0 || !baseLocation) return [];
  const minsBetween = (a, b) => (a && b ? Math.abs(new Date(b) - new Date(a)) / 60000 : 0);
  const rawAtBase = points.map((p) => haversineMiles(p.lat, p.lng, baseLocation.lat, baseLocation.lng) <= AT_BASE_RADIUS_MILES);

  // A run of consecutive same-reading points shorter than MIN_STATE_DWELL_MINUTES is
  // folded into whichever reading came right before it, instead of counting as a
  // genuine state change. Without this, one fleeting GPS ping near the depot (or a
  // brief GPS jump AWAY from it while the vehicle is genuinely still parked) gets
  // misread as "returned to base and set off again", wrongly recolouring the rest of
  // the SAME still-in-progress run as if it were the next one.
  const confirmed = rawAtBase.slice();
  let i = 0;
  while (i < confirmed.length) {
    let j = i;
    while (j < confirmed.length && confirmed[j] === confirmed[i]) j++;
    // Dwell duration is measured up to the START of the NEXT differing reading (not the
    // last point still inside this run, which is the same point — and so ~0 duration —
    // for a single-point run). The trailing run (nothing after it yet) is never folded:
    // there's no future data to judge it fleeting, and it's the vehicle's current,
    // still-in-progress state.
    const dwellMinutes = j < confirmed.length ? minsBetween(points[i].time, points[j].time) : Infinity;
    if (i > 0 && dwellMinutes < MIN_STATE_DWELL_MINUTES) {
      for (let k = i; k < j; k++) confirmed[k] = confirmed[i - 1];
    }
    i = j;
  }

  let state = confirmed[0] ? 'at_base' : 'away';
  let cycle = 0;
  let hasDepartedOnce = state === 'away';
  let lastDepartureTime = hasDepartedOnce ? points[0].time : null;
  let lastReturnTime = null;
  return confirmed.map((atBaseNow, idx) => {
    const t = points[idx].time;
    if (state === 'at_base' && !atBaseNow) {
      // Only advance the colour if the excursion that JUST ended (lastDepartureTime ->
      // lastReturnTime) actually reached at least one stop — otherwise it wasn't a real
      // run, and this new departure continues the same one that's still in progress.
      if (hasDepartedOnce && lastReturnTime) {
        const madeProgress = completedStopTimes.some((ct) => ct >= lastDepartureTime && ct <= lastReturnTime);
        if (madeProgress) cycle += 1;
      }
      hasDepartedOnce = true;
      lastDepartureTime = t;
      state = 'away';
    } else if (state === 'away' && atBaseNow) {
      lastReturnTime = t;
      state = 'at_base';
    }
    return cycle;
  });
};

const buildBreadcrumbSegments = (points, baseLocation, completedStops, colors, markLastSegmentSolid) => {
  if (!points || points.length < 2 || !baseLocation) return [];
  const runIndexByPoint = computeRunCycleByPoint(points, baseLocation, completedStops.map((s) => s.time));

  const lastStopTime = markLastSegmentSolid && completedStops.length > 0
    ? completedStops.reduce((max, s) => (!max || s.time > max ? s.time : max), null)
    : null;

  const segments = [];
  let current = [points[0]];
  let currentRunIdx = runIndexByPoint[0];
  for (let i = 1; i < points.length; i++) {
    const crossedStopBoundary = lastStopTime && points[i - 1].time <= lastStopTime && points[i].time > lastStopTime;
    current.push(points[i]);
    if (runIndexByPoint[i] !== currentRunIdx || crossedStopBoundary) {
      segments.push({ path: current.map((p) => [p.lat, p.lng]), color: colors[currentRunIdx % colors.length], endTime: current[current.length - 1].time });
      current = [points[i]];
      currentRunIdx = runIndexByPoint[i];
    }
  }
  segments.push({ path: current.map((p) => [p.lat, p.lng]), color: colors[currentRunIdx % colors.length], endTime: current[current.length - 1].time });
  // No live-position concept at all (a finished/past day, `markLastSegmentSolid` false) —
  // the whole thing is history, so dash everything. Otherwise, only what's before/at the
  // last genuinely-reached stop is dashed; nothing reached yet today (`lastStopTime` still
  // null) correctly means nothing is dashed yet either — it's all still the current leg.
  return segments.map((seg) => ({
    ...seg,
    dashed: true, // the driven trail is ALWAYS dashed; only the forward "route ahead" line is solid
  }));
};

/** Renders one breadcrumb segment — thin, consistent width throughout (no
 *  tapering), dashed for "already fully driven" history or solid for the
 *  active current leg, always in that segment's own run colour. The driven trail
 *  is drawn bold (3.5px, fully opaque) over a dark outline, so it stands clear of
 *  the roads underneath at every zoom instead of sinking into them. */
const BreadcrumbSegment = ({ path, color, dashed }) => (
  dashed
    ? (
      <>
        {/* A dark outline under the coloured dashes, so the trail reads against any road colour */}
        <Polyline positions={path} pathOptions={{ color: '#000000', weight: 6.5, opacity: 0.6, lineCap: 'round', lineJoin: 'round' }} />
        <Polyline positions={path} pathOptions={{ color, weight: 3.5, opacity: 1, dashArray: '8 6', lineCap: 'round', lineJoin: 'round' }} />
      </>
    )
    : (
      <>
        <Polyline positions={path} pathOptions={{ color, weight: 5, opacity: 0.2, lineCap: 'round' }} />
        <Polyline positions={path} pathOptions={{ color, weight: 3, opacity: 0.95, lineCap: 'round' }} />
      </>
    )
);

/**
 * Dark, live Transport map — reuses the same `/api/postcode/{code}` geocoding proxy
 * as the existing Areas map. Shows a home pin at the branch postcode, 25/50mi rings,
 * and (when a vehicle is passed in) one curved, drop-shadowed, direction-arrowed
 * route line per run — Run 1 Orange, Run 2 Purple, Run 3 Pink, Run 4 Blue (cycles
 * for any additional runs) — plus a coloured pin per stop matching its run.
 *
 * `dateMode` changes what's overlaid on top of the same base map/home pin/rings:
 *  - 'future' (default): unchanged — the planned route + stop pins, as above.
 *  - 'today': the planned ROUTE LINE is hidden (replaced by a live truck marker, its
 *    full day's breadcrumb trail — see `buildBreadcrumbSegments` — and a single
 *    curved/arrow-headed "ahead of vehicle" line to the immediate next stop only,
 *    revealed one stop at a time as the run progresses) but the stop pins stay
 *    visible so staff can still see where the driver has been vs. still has to go.
 *  - 'past': fetches that vehicle's actual completed Samsara path for `historyDate`
 *    and renders the exact same run-coloured breadcrumb trail as 'today' — a
 *    finished day looks identical whether it's still technically "today" after
 *    hours or genuinely a past date, since it's the same historical query either way.
 */
const TransportMapPanel = ({ basePostcode, baseLocationOverride, vehicle, className = 'relative w-full h-full', trackingUrl, onOpenTracking, dateMode = 'future', historyDate, liveVehicle, atDropEntryId, currentBranchId, hideLegend = false, focusEntryId = null, focusNonce = 0 }) => {
  const [baseLocation, setBaseLocation] = useState(null);
  const [runRoutes, setRunRoutes] = useState([]); // [{ label, color, points: [{id,lat,lng,customer,postcode,order,time,color}] }]
  const [loadingRoute, setLoadingRoute] = useState(false);
  const [historyPath, setHistoryPath] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [breadcrumbPath, setBreadcrumbPath] = useState([]);

  // A postcode clicked on a Transport entry (see TransportOrderCard / TransportNewUI): once that stop's dot is on the map, give
  // it a pulsing beacon for a few seconds and open its popup, exactly as if the dot itself had been clicked.
  const markerRefs = useRef({});
  const [beaconEntryId, setBeaconEntryId] = useState(null);
  const [focusTarget, setFocusTarget] = useState(null); // { lat, lng, key } — where MapFocus should centre the map
  const handledFocusRef = useRef(0);
  const beaconTimerRef = useRef(null);
  useEffect(() => () => { if (beaconTimerRef.current) clearTimeout(beaconTimerRef.current); }, []);
  useEffect(() => {
    if (!focusEntryId || !focusNonce || handledFocusRef.current === focusNonce) return;
    const marker = markerRefs.current[focusEntryId];
    if (!marker) return; // the stops are still being plotted — this runs again as soon as they are
    handledFocusRef.current = focusNonce;
    setBeaconEntryId(focusEntryId);
    if (beaconTimerRef.current) clearTimeout(beaconTimerRef.current);
    beaconTimerRef.current = setTimeout(() => setBeaconEntryId(null), 8000);
    marker.openPopup();
    const markerPosition = marker.getLatLng();
    setFocusTarget({ lat: markerPosition.lat, lng: markerPosition.lng, key: focusNonce });
  }, [focusEntryId, focusNonce, runRoutes]);

  // A manually-pinned exact base location (Settings > Vehicles > Base Location) always
  // wins over the postcode geocode — a postcode's geocoded centroid can be meaningfully
  // off from the actual depot entrance/yard the vehicles really park at.
  useEffect(() => {
    if (baseLocationOverride?.lat != null && baseLocationOverride?.lng != null) {
      setBaseLocation({ lat: baseLocationOverride.lat, lng: baseLocationOverride.lng });
      return;
    }
    if (!basePostcode) return;
    fetchLocation(basePostcode).then((loc) => loc && setBaseLocation(loc));
  }, [basePostcode, baseLocationOverride]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!vehicle) {
        setRunRoutes([]);
        return;
      }
      setLoadingRoute(true);
      const runsList = [
        { label: 'Run 1', color: RUN_COLORS[0], entries: vehicle.entries || [] },
        ...(vehicle.runs || []).map((r, i) => ({
          label: `Run ${r.run_number || i + 2}`,
          color: RUN_COLORS[(i + 1) % RUN_COLORS.length],
          entries: r.entries || [],
        })),
      ];
      const resolved = [];
      for (const runInfo of runsList) {
        const points = [];
        for (const [entryIndex, entry] of orderRunEntries(runInfo.entries).entries()) {
          if (!entry.postcode) continue;
          // A confirmed stopped-arrival (services/samsara_geofence.py) snaps this entry's
          // known location to the vehicle's actual live GPS fix, permanently — a postcode's
          // geocoded centroid can sit meaningfully off from where the vehicle genuinely
          // parks for this specific customer, so that real correction always wins here.
          const loc = (entry.actual_lat != null && entry.actual_lng != null)
            ? { lat: entry.actual_lat, lng: entry.actual_lng }
            : await fetchLocation(entry.postcode);
          // Always push a point, even when geocoding genuinely fails (invalid/unrecognised
          // postcode) — `lat`/`lng` land as `null` and every render site below skips drawing
          // a pin/line for it, but its POSITION in this run's points array is preserved.
          // Silently omitting failed-geocode stops here previously shifted every later
          // stop's index down by one, which corrupted `nextStopIsFirstOfItsRun`'s
          // `points[0]` check (see the render body) — a run's real 2nd stop could get
          // mistaken for its 1st the moment its real 1st stop failed to geocode, wrongly
          // drawing a "driving back to base" leg for a run that was simply continuing.
          points.push({
            id: entry.id, color: runInfo.color, status: entry.status, completedAt: entry.completed_at, stopNumber: entryIndex + 1,
            lat: loc?.lat ?? null, lng: loc?.lng ?? null,
            customer: entry.customer_name, postcode: entry.postcode,
            order: entry.order_number, time: entry.time_slot,
          });
        }
        if (points.length > 0) resolved.push({ label: runInfo.label, color: runInfo.color, points });
      }
      if (!cancelled) {
        setRunRoutes(resolved);
        setLoadingRoute(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [vehicle]);

  // Past-day completed route — only fetched when actually needed. Looked up by the
  // vehicle's stable registry `id` (not its registration string, which can be renamed
  // in Settings after a day's board was already seeded). Kept as full {lat,lng,time}
  // objects (not just [lat,lng]) — buildBreadcrumbSegments needs the timestamps to
  // colour-band by run and dash everything before the day's last reached stop.
  useEffect(() => {
    let cancelled = false;
    if (dateMode !== 'past' || !vehicle?.id || !historyDate) {
      setHistoryPath([]);
      return;
    }
    setLoadingHistory(true);
    const trackedId = vehicle.registry_vehicle_id || vehicle.id;
    api.get(`${API}/transport/samsara/history/${encodeURIComponent(trackedId)}/${historyDate}?view_as_branch=${encodeURIComponent(currentBranchId || '')}&registration=${encodeURIComponent(vehicle.vehicle_registration || '')}`)
      .then((res) => {
        if (cancelled) return;
        setHistoryPath(res.data?.points || []);
      })
      .catch(() => { if (!cancelled) setHistoryPath([]); })
      .finally(() => { if (!cancelled) setLoadingHistory(false); });
    return () => { cancelled = true; };
  }, [dateMode, vehicle?.id, vehicle?.registry_vehicle_id, vehicle?.vehicle_registration, historyDate, currentBranchId]);

  // Today's breadcrumb trail — "where the vehicle has actually been so far today", reusing
  // the exact same Samsara history endpoint (querying today's date just naturally returns
  // whatever breadcrumbs exist up to now) — refreshed every 60s as the day progresses. Stays
  // fully queryable (and gets rendered exactly the same way) even once the vehicle goes
  // offline or every run for the day is finished — it's a historical query, not tied to a
  // "live" cache, so today's full path never disappears just because tracking itself has.
  useEffect(() => {
    let cancelled = false;
    if (dateMode !== 'today' || !vehicle?.id || !historyDate) {
      setBreadcrumbPath([]);
      return;
    }
    const trackedId = vehicle.registry_vehicle_id || vehicle.id;
    const fetchBreadcrumb = () => {
      api.get(`${API}/transport/samsara/history/${encodeURIComponent(trackedId)}/${historyDate}?view_as_branch=${encodeURIComponent(currentBranchId || '')}&registration=${encodeURIComponent(vehicle.vehicle_registration || '')}`)
        .then((res) => { if (!cancelled) setBreadcrumbPath(res.data?.points || []); })
        .catch(() => { if (!cancelled) setBreadcrumbPath([]); });
    };
    fetchBreadcrumb();
    const interval = setInterval(fetchBreadcrumb, 60000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [dateMode, vehicle?.id, vehicle?.registry_vehicle_id, vehicle?.vehicle_registration, historyDate, currentBranchId]);

  if (!baseLocation) {
    return (
      <div className={`flex items-center justify-center bg-slate-950 ${className}`}>
        <span className="text-slate-500 text-sm">Loading map…</span>
      </div>
    );
  }

  const allPoints = runRoutes.flatMap((r) => r.points);
  // Only points that actually resolved a real lat/lng feed anything drawn on the map
  // itself (pins, fit-bounds, route lines) — `allPoints` still keeps failed-geocode
  // entries (see the point-building effect above) purely so their POSITION in each
  // run's array stays correct for the structural checks below.
  const geocodedPoints = allPoints.filter((p) => p.lat != null && p.lng != null);
  // Which branches today's/this run's stops actually touch — a collection or delivery
  // at another branch's address should make that branch's map marker stand out; every
  // other branch marker stays dimmed so it doesn't clutter the map with irrelevant noise.
  const activeStopPostcodes = new Set(
    allPoints.filter((p) => p.postcode).map((p) => p.postcode.replace(/\s+/g, '').toUpperCase())
  );
  const showPlannedRoute = dateMode !== 'today' && dateMode !== 'past';
  // Only changes on a genuine vehicle/day switch — NOT on every live-position poll or
  // silent board refresh — so MapAutoFit knows when it's actually allowed to re-fit
  // (see its own comment above).
  const mapFitKey = `${vehicle?.id || 'none'}-${dateMode}-${historyDate || ''}`;
  // The specific stop the truck has genuinely arrived+stopped at (if any) — its static
  // pin is hidden below while the truck itself takes on that stop's colour instead, so
  // the coloured dot visually "moves into" the truck rather than sitting duplicated
  // next to it.
  const atDropPoint = atDropEntryId ? allPoints.find((p) => p.id === atDropEntryId) : null;
  const vehicleAtBase = dateMode === 'today' && !!liveVehicle && !!baseLocation &&
    haversineMiles(liveVehicle.lat, liveVehicle.lng, baseLocation.lat, baseLocation.lng) <= AT_BASE_RADIUS_MILES;
  // Only GPS-confirmed geofence arrivals (`actual_lat`/`actual_lng` + `completed_at`, set by
  // services/samsara_geofence.py) — not manual "mark complete" clicks, which have no real
  // position/time to place on the trail — feed the breadcrumb's colour/dash segmentation.
  const completedStops = allPoints.filter((p) => isEntryComplete({ status: p.status, completed_at: p.completedAt }) && p.completedAt).map((p) => ({ time: p.completedAt }));
  // The single next stop the vehicle is heading to — first non-terminal entry across every
  // run, in run order — drives both the "ahead of vehicle" route-reveal line and its colour.
  const nextStopPoint = allPoints.find((p) => !isEntryFinished({ status: p.status, completed_at: p.completedAt })) || null;
  // True the moment that next stop is the FIRST stop of a run other than the very first one
  // (i.e. the vehicle has just finished every stop on a previous run) and it hasn't actually
  // reached base yet — the "ahead of vehicle" line must then go vehicle -> base -> next stop,
  // never a straight line skipping the depot as if the driver went directly on to the next run.
  const nextStopRunIndex = nextStopPoint
    ? runRoutes.findIndex((r) => r.points.some((p) => p.id === nextStopPoint.id))
    : -1;
  const nextStopIsFirstOfItsRun = nextStopRunIndex >= 0 && runRoutes[nextStopRunIndex].points[0]?.id === nextStopPoint?.id;
  // Real GPS out-and-back cycle the vehicle is CURRENTLY physically on (see
  // computeRunCycleByPoint above) — if it's already >= the next stop's scheduled run
  // index, the driver has ALREADY made the return-to-base-and-departed-again trip for
  // real, even though that stop itself hasn't been reached yet. In that case the
  // "return to base" leg below must NOT be drawn again — it already happened; only the
  // direct leg onward to the next stop is correct.
  const physicalCycleByPoint = dateMode === 'today' ? computeRunCycleByPoint(breadcrumbPath, baseLocation, completedStops.map((s) => s.time)) : [];
  const currentPhysicalCycle = physicalCycleByPoint.length > 0 ? physicalCycleByPoint[physicalCycleByPoint.length - 1] : null;
  const hasAlreadyDepartedForNextRun = currentPhysicalCycle !== null && currentPhysicalCycle >= nextStopRunIndex;
  const needsBaseReturnLeg = nextStopIsFirstOfItsRun && nextStopRunIndex > 0 && !vehicleAtBase && !hasAlreadyDepartedForNextRun;
  const activeBreadcrumbPoints = dateMode === 'today' ? breadcrumbPath : (dateMode === 'past' ? historyPath : []);
  const breadcrumbSegments = buildBreadcrumbSegments(
    activeBreadcrumbPoints, baseLocation, completedStops, RUN_COLORS, dateMode === 'today' && !!liveVehicle
  );

  return (
    <div className={className} data-testid="transport-live-map">
      <style>{BEACON_CSS}</style>
      <MapContainer
        center={[baseLocation.lat, baseLocation.lng]}
        zoom={9}
        style={{ height: '100%', width: '100%', background: '#0f172a' }}
        zoomControl={false}
        fadeAnimation={false}
        attributionControl={false}
        scrollWheelZoom
        dragging
        doubleClickZoom
        touchZoom
      >
        <TileLayer className="transport-map-bright-tiles" eventHandlers={{ tileerror: retryFailedTile }} url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}.png?key=cb1_2uap_1_93da0402da1fce587cd7c547" attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>' />
        <MapAutoFit base={baseLocation} points={geocodedPoints} fitKey={mapFitKey} focusNonce={focusNonce} />
        <MapFocus target={focusTarget} />
        <MapSizeWatcher />
        <BranchMarkersLayer currentBranchId={currentBranchId} activeStopPostcodes={activeStopPostcodes} />

        <Circle
          center={[baseLocation.lat, baseLocation.lng]}
          radius={50 * MILES_TO_METRES}
          pathOptions={{ color: '#475569', fill: false, fillOpacity: 0, weight: 1.5, dashArray: '6 6', interactive: false }}
        />
        <Circle
          center={[baseLocation.lat, baseLocation.lng]}
          radius={25 * MILES_TO_METRES}
          pathOptions={{ color: '#64748b', fill: false, fillOpacity: 0, weight: 1.5, dashArray: '4 4', interactive: false }}
        />

        {showPlannedRoute && runRoutes.map((r, ri) => {
          const geocoded = r.points.filter((p) => p.lat != null && p.lng != null);
          if (geocoded.length === 0) return null;
          const rawPath = [[baseLocation.lat, baseLocation.lng], ...geocoded.map((p) => [p.lat, p.lng])];
          const curvedPath = buildCurvedPath(rawPath);
          return <RouteLine key={ri} path={curvedPath} color={r.color} />;
        })}

        {/* Full-day breadcrumb trail — coloured by run/out-and-back cycle, dashed for
            everything already fully driven except the current active leg (solid). Stays
            rendered exactly the same way for a finished 'today' (tracking stopped, all runs
            done) and for genuinely 'past' days alike — it's a historical query either way,
            so the full multi-coloured record never disappears just because live tracking has
            stopped (see the fetch effects above). */}
        {breadcrumbSegments.map((seg, i) => <BreadcrumbSegment key={i} path={seg.path} color={seg.color} dashed={seg.dashed} />)}

        {/* "Ahead of vehicle" reveal — the existing curved/arrow-headed planned-route style,
            but only ever drawn for the ONE immediate next stop, in that stop's own run
            colour. Every stop beyond it stays a plain dot (below) until the vehicle actually
            reaches this one, at which point the line reveals for the next segment in turn.
            Once a run is fully finished, the driver ALWAYS heads back to base before setting
            off on the next run — so that leg renders as its own neutral grey/white dashed
            line (never the next run's colour) before the next run's own coloured leg picks
            up from base. */}
        {/* Every stop of the day is done and the truck is still out: a green dashed arrowed line from the truck back to base. */}
        {dateMode === 'today' && liveVehicle && !vehicleAtBase && !nextStopPoint && allPoints.length > 0 && (
          <RouteLine
            path={buildCurvedPath([[liveVehicle.lat, liveVehicle.lng], [baseLocation.lat, baseLocation.lng]])}
            color={RETURN_TO_BASE_COLOR}
            dashed
          />
        )}

        {dateMode === 'today' && liveVehicle && nextStopPoint && nextStopPoint.lat != null && nextStopPoint.lng != null && (
          needsBaseReturnLeg ? (
            <>
              <RouteLine
                path={buildCurvedPath([[liveVehicle.lat, liveVehicle.lng], [baseLocation.lat, baseLocation.lng]])}
                color={RETURN_TO_BASE_COLOR}
                dashed
              />
              <RouteLine
                path={buildCurvedPath([[baseLocation.lat, baseLocation.lng], [nextStopPoint.lat, nextStopPoint.lng]])}
                color={nextStopPoint.color}
              />
            </>
          ) : (
            <RouteLine
              path={buildCurvedPath([[liveVehicle.lat, liveVehicle.lng], [nextStopPoint.lat, nextStopPoint.lng]])}
              color={nextStopPoint.color}
            />
          )
        )}

        {/* Stop pins stay visible in every mode (including 'today') so staff can always
            see where the driver has been vs. still has to go. One that the truck has
            genuinely arrived+stopped at sits at the truck's real GPS fix rather than the
            geocoded postcode (see the point-building effect above) — the truck itself
            turns this same colour while it's here, then reverts to blue once it moves on,
            leaving the now-corrected pin exactly where it truly was. */}
        {runRoutes.map((r) => r.points.map((p, i) => {
          if (p.lat == null || p.lng == null) return null; // failed geocode — nothing to place a pin at
          const stopComplete = isEntryComplete({ status: p.status, completed_at: p.completedAt });
          const beaconOn = beaconEntryId === p.id;
          return (
            <Marker
              key={`${r.label}-${p.id || i}`}
              ref={(m) => { if (m) markerRefs.current[p.id] = m; else delete markerRefs.current[p.id]; }}
              position={[p.lat, p.lng]}
              icon={getStopIcon(r.color, p.stopNumber, stopComplete, beaconOn)}
              zIndexOffset={beaconOn ? 900 : 0}
            >
              <Popup>
                <div className="text-xs">
                  <strong>{p.customer || 'Stop'}</strong><br />
                  <span style={{ color: r.color, fontWeight: 700 }}>{r.label}{p.stopNumber ? ` · Stop ${p.stopNumber}` : ''}</span>
                  {stopComplete && <span style={{ color: '#16a34a', fontWeight: 700 }}> · Completed</span>}<br />
                  {p.postcode}{p.order && <> · #{p.order}</>}{p.time && <> · {p.time}</>}
                </div>
              </Popup>
            </Marker>
          );
        }))}

        {dateMode === 'today' && liveVehicle && (() => {
          const haloColor = atDropPoint?.color || null;
          const stateLabel = atDropPoint ? `At a drop — ${atDropPoint.customer || 'stop'}` : (vehicleAtBase ? 'Back at base' : 'En route');
          const regSuffix = (vehicle?.vehicle_registration || '').slice(-3) || null;
          return (
            <Marker position={[liveVehicle.lat, liveVehicle.lng]} icon={buildLiveTruckIcon(liveVehicle.heading, haloColor, vehicleAtBase, regSuffix)} zIndexOffset={1000}>
              <Popup>
                <div className="text-xs">
                  <strong>{vehicle?.vehicle_registration || 'Vehicle'}</strong> — {stateLabel}<br />
                  {liveVehicle.address && <>{liveVehicle.address}<br /></>}
                  {liveVehicle.speed != null && <>{Math.round(liveVehicle.speed)} mph · </>}
                  {liveVehicle.updated_at && <>Updated {new Date(liveVehicle.updated_at).toLocaleTimeString('en-GB')}</>}
                </div>
              </Popup>
            </Marker>
          );
        })()}

        {/* Static home pin — hidden while the live truck marker is itself sitting at base
            (house-glyph, blue+LIVE) so the two don't visually stack on top of each other. */}
        {!vehicleAtBase && (
          <Marker position={[baseLocation.lat, baseLocation.lng]} icon={homeIcon}>
            <Popup>Branch base</Popup>
          </Marker>
        )}
      </MapContainer>
      {vehicle && trackingUrl && (
        <button
          type="button"
          onClick={() => onOpenTracking?.(vehicle, trackingUrl)}
          className="absolute top-32 left-3 md:left-[366px] z-[500] flex items-center gap-1.5 px-3 py-1.5 rounded-full text-white text-xs font-bold shadow-lg hover:brightness-110 transition-all"
          style={{ backgroundColor: PURPLE }}
          title="Track this vehicle"
          data-testid={`transport-map-tracking-btn-${vehicle.id}`}
        >
          <Radar className="w-3.5 h-3.5" />
          Track
        </button>
      )}
      {dateMode === 'today' && vehicle && !liveVehicle && (
        <div className="absolute top-3 right-3 bg-black/60 text-amber-300 text-xs px-2.5 py-1 rounded-full z-[500]" data-testid="transport-map-no-live-signal">
          No live Samsara signal for this vehicle
        </div>
      )}
      {(loadingRoute || loadingHistory) && (
        <div className="absolute top-3 right-3 bg-black/60 text-white text-xs px-2.5 py-1 rounded-full z-[500]">
          {loadingHistory ? 'Loading route history…' : 'Plotting route…'}
        </div>
      )}
      {dateMode === 'past' && !loadingHistory && historyPath.length === 0 && vehicle && (
        <div className="absolute top-3 right-3 bg-black/60 text-slate-300 text-xs px-2.5 py-1 rounded-full z-[500]" data-testid="transport-map-no-history">
          No Samsara route history found for this day
        </div>
      )}
      {runRoutes.length > 0 && !hideLegend && (
        <div className="absolute bottom-3 right-3 bg-black/60 backdrop-blur text-white text-[11px] px-3 py-2 rounded-xl z-[500] flex flex-col gap-1">
          {(dateMode === 'today' || dateMode === 'past') && (
            <div className="flex flex-col gap-1 pb-1 mb-0.5 border-b border-white/15">
              {dateMode === 'today' && (
                <>
                  <div className="flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-full inline-block" style={{ backgroundColor: LIVE_BLUE }} />
                    Live position (en route)
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-full inline-block border-2 border-white/70" style={{ background: 'conic-gradient(from 0deg, #a855f7, #ec4899, #3b82f6, #a855f7)' }} />
                    Truck matches + snaps that stop's pin to its real spot
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Home size={11} className="text-blue-400" />
                    Home icon = parked back at base
                  </div>
                </>
              )}
              <div className="flex items-center gap-1.5">
                <span className="w-4 h-0.5 rounded inline-block" style={{ backgroundColor: '#f97316' }} />
                Driven path (coloured per run)
              </div>
              {dateMode === 'today' ? (
                <div className="flex items-center gap-1.5">
                  <span className="w-4 h-0 inline-block" style={{ borderTop: '2px dashed rgba(255,255,255,0.6)' }} />
                  Dashed = trail already driven · solid arrow = route ahead · green = heading back to base
                </div>
              ) : (
                <div className="flex items-center gap-1.5">
                  <span className="w-4 h-0 inline-block" style={{ borderTop: '2px dashed rgba(255,255,255,0.6)' }} />
                  Dashed = distance driven that day
                </div>
              )}
              <div className="flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-full inline-block border-2 border-green-500" />
                Green dot = drop completed (the number is the stop order)
              </div>
            </div>
          )}
          {runRoutes.map((r) => (
            <div key={r.label} className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full inline-block" style={{ backgroundColor: r.color }} />
              {r.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TransportMapPanel;
